/**
 * 举报域 API（FR-9 / 举报违规服务域）。提交举报 + 举报证据图直传凭证。
 * 调既有 `POST /api/v1/risk/reports`（Story 2.9 + 15-4；Story 30-3 扩 PLATFORM 平台级投诉）。
 * 直传语义对齐 `review.ts uploadReviewImage`：mock 实装 `uploadUrl===finalUrl` → 跳过 PUT；
 * 真实 MinIO 下 presigned PUT URL → PUT 文件字节。Web 用浏览器原生 File（_shared C-4 正向差异）。
 * 鉴权 JWT + `@IdentityStateRequired(REGISTERED)`：调用前经 `useAuthRequired` 把关。
 */
import { call } from './client';
import type { components } from './openapi.generated';

type Schemas = components['schemas'];

/** 举报类型 6 枚举 —— 与后端 `com.workability.client.report.enums.ReportType` 严格对齐。 */
export type ReportTypeCode =
  | 'FALSE_INFO'
  | 'PORN_VIOLENCE'
  | 'FRAUD'
  | 'ABNORMAL_PRICE'
  | 'REGULATORY_SENSITIVE'
  | 'OTHER';

/**
 * 举报对象类型 —— 与后端 `com.workability.client.report.enums.ReportTargetType` 对齐。
 * PRODUCT=商品 / SELLER=卖家（Story 15-4）/ PLATFORM=平台级投诉，无对象（Story 30-3）。
 */
export type ReportTargetTypeCode = 'PRODUCT' | 'SELLER' | 'PLATFORM';

/** 举报类型 6 项：code 对齐后端枚举，label 取 epics Story 2.9 AC 文案。 */
export const REPORT_TYPES: { code: ReportTypeCode; label: string }[] = [
  { code: 'FALSE_INFO', label: '虚假信息' },
  { code: 'PORN_VIOLENCE', label: '涉黄涉黑' },
  { code: 'FRAUD', label: '诈骗' },
  { code: 'ABNORMAL_PRICE', label: '价格异常' },
  { code: 'REGULATORY_SENSITIVE', label: '监管敏感' },
  { code: 'OTHER', label: '其他' },
];

/** 举报说明字数上限（与后端 `@Size(min=1,max=200)` 一致，不放宽）。 */
export const REPORT_DESC_MAX = 200;
/** 举报证据图张数上限（与后端 `@Size(max=3)` 一致）。 */
export const REPORT_SCREENSHOT_MAX = 3;

/** 举报业务错误码（语义引后端 SubmitReportUseCase / ReportService）。 */
export const REPORT_ERROR = {
  DUPLICATE_24H: '020301',
  CONTENT_VIOLATION: '020302',
  INVALID_TYPE: '020303',
  INVALID_TARGET_TYPE: '020304',
  PRODUCT_NOT_FOUND: '020001',
  PRODUCT_OFFLINE: '020003',
  SELLER_NOT_FOUND: '020305',
  SELF_REPORT: '020306',
} as const;

/** 举报提交入参（对齐后端 SubmitReportRequest；PLATFORM 省略 targetId）。 */
export interface SubmitReportInput {
  targetType: ReportTargetTypeCode;
  /** 被举报对象 id：PRODUCT=商品 id / SELLER=卖家 user id；PLATFORM 无对象，省略。 */
  targetId?: string;
  type: ReportTypeCode;
  /** 文字说明，1–200 字（前端约束，后端 `@Size` 双校验）。 */
  description: string;
  /** 证据图 finalUrl，0–3 张。 */
  screenshots?: string[];
}

/** 提交举报。业务失败抛 ServerError（带 code，见 REPORT_ERROR）。 */
export function submitReport(input: SubmitReportInput): Promise<Schemas['SubmitReportResponse']> {
  const body: Schemas['SubmitReportRequest'] = {
    targetType: input.targetType,
    // PLATFORM 无对象：不传 targetId（后端归一为 NULL）
    ...(input.targetId ? { targetId: input.targetId } : {}),
    type: input.type,
    description: input.description,
    screenshots: input.screenshots ?? [],
  };
  return call<Schemas['SubmitReportResponse']>({
    method: 'POST',
    url: '/api/v1/risk/reports',
    data: body,
  });
}

/** 签发举报证据图直传凭证（选图前调用，身份态 ≥ REGISTERED）。 */
export function getReportEvidenceUploadCredential(): Promise<
  Schemas['ReportEvidenceUploadCredentialResponse']
> {
  return call<Schemas['ReportEvidenceUploadCredentialResponse']>({
    method: 'GET',
    url: '/api/v1/risk/reports/upload-credential',
  });
}

/**
 * 上传一张举报证据图到对象存储，返回 finalUrl（写入 screenshots）。
 * mock（uploadUrl===finalUrl）直接返回 finalUrl，不发 PUT（对齐 review.ts）。
 */
export async function uploadReportEvidence(file: File): Promise<string> {
  const cred = await getReportEvidenceUploadCredential();
  if (!cred.uploadUrl || !cred.finalUrl) {
    throw new Error('上传凭证无效');
  }
  if (cred.uploadUrl === cred.finalUrl) {
    return cred.finalUrl; // mock 实装:无需真实 PUT
  }
  const resp = await fetch(cred.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'image/jpeg' },
  });
  if (!resp.ok) {
    throw new Error(`证据图上传失败:${resp.status}`);
  }
  return cred.finalUrl;
}
